import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Animated premium line chart for the homepage hero.
 * - Soft hex/grid backdrop is rendered by the parent.
 * - Pure SVG so it scales crisply and stays lightweight.
 * - Live feel: line draws in, pulses, traveling marker, scanning sweep,
 *   subtly drifting series and background bars.
 */
export function HeroChart({ className = "" }: { className?: string }) {
  // Seed series — an upward-drifting walk that ends near the top right.
  const base = useMemo(
    () => [44, 48, 42, 46, 40, 44, 38, 42, 36, 44, 40, 48, 44, 52, 56, 62, 70, 78, 88],
    [],
  );

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 2200);
    return () => clearInterval(id);
  }, []);

  // Tiny drift per tick so the line feels alive without being noisy.
  const series = useMemo(() => {
    return base.map((v, i) => {
      const wobble = Math.sin((tick + i) * 0.6) * 1.6;
      return Math.max(20, Math.min(96, v + wobble));
    });
  }, [base, tick]);

  const W = 1200;
  const H = 360;
  const pad = { l: 20, r: 20, t: 20, b: 30 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const points = series.map((v, i) => {
    const x = pad.l + (i / (series.length - 1)) * innerW;
    const y = pad.t + innerH - (v / 100) * innerH;
    return { x, y };
  });

  const path = points
    .map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`))
    .join(" ");
  const areaPath = `${path} L${pad.l + innerW},${pad.t + innerH} L${pad.l},${pad.t + innerH} Z`;

  const last = points[points.length - 1];

  // Measure path length for draw-on animation + traveling marker.
  const lineRef = useRef<SVGPathElement | null>(null);
  const [pathLen, setPathLen] = useState(0);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    if (lineRef.current) {
      const L = lineRef.current.getTotalLength();
      setPathLen(L);
      // trigger draw-in on next frame
      requestAnimationFrame(() => setDrawn(true));
    }
  }, []);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`h-auto w-full ${className}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="hero-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#00D4FF" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="hero-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#5EE0FF" />
          <stop offset="100%" stopColor="#00D4FF" />
        </linearGradient>
        <linearGradient id="hero-sweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#5EE0FF" stopOpacity="0" />
          <stop offset="50%" stopColor="#5EE0FF" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#5EE0FF" stopOpacity="0" />
        </linearGradient>
        <filter id="hero-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Faint grid */}
      <g stroke="#0fb6e5" strokeOpacity="0.08" strokeWidth="1">
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={pad.l} x2={pad.l + innerW} y1={pad.t + innerH * f} y2={pad.t + innerH * f} />
        ))}
      </g>

      {/* Background data bars (subtle market motion) */}
      <g>
        {Array.from({ length: 32 }).map((_, i) => {
          const x = pad.l + (i / 31) * innerW;
          const h = 14 + (Math.sin((tick + i) * 0.9) + 1) * 28;
          return (
            <rect
              key={i}
              x={x - 2}
              y={pad.t + innerH - h}
              width="3"
              height={h}
              fill="#00D4FF"
              opacity="0.06"
              rx="1"
            >
              <animate
                attributeName="opacity"
                values="0.04;0.14;0.04"
                dur={`${3 + (i % 5) * 0.4}s`}
                repeatCount="indefinite"
                begin={`${(i % 8) * 0.15}s`}
              />
            </rect>
          );
        })}
      </g>

      {/* Scanning sweep */}
      <rect
        x={pad.l}
        y={pad.t}
        width={innerW * 0.18}
        height={innerH}
        fill="url(#hero-sweep)"
        opacity="0.7"
      >
        <animate
          attributeName="x"
          values={`${pad.l - innerW * 0.2};${pad.l + innerW}`}
          dur="6s"
          repeatCount="indefinite"
        />
      </rect>

      {/* Filled area */}
      <path
        d={areaPath}
        fill="url(#hero-area)"
        style={{
          transition: "d 1.4s ease-in-out, opacity 1.2s ease-out",
          opacity: drawn ? 1 : 0,
        }}
      />

      {/* Line */}
      <path
        ref={lineRef}
        d={path}
        fill="none"
        stroke="url(#hero-line)"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter="url(#hero-glow)"
        style={{
          transition: "d 1.4s ease-in-out, stroke-dashoffset 2.2s ease-out",
          strokeDasharray: pathLen || undefined,
          strokeDashoffset: drawn ? 0 : pathLen,
        }}
      />

      {/* Traveling marker along the line */}
      {pathLen > 0 && (
        <circle r="4" fill="#FFFFFF" opacity="0.9" filter="url(#hero-glow)">
          <animateMotion dur="5s" repeatCount="indefinite" path={path} rotate="auto" />
          <animate attributeName="opacity" values="0;0.9;0" dur="5s" repeatCount="indefinite" />
        </circle>
      )}

      {/* Nodes */}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="3.2"
          fill="#0a1428"
          stroke="#5EE0FF"
          strokeWidth="1.6"
          style={{ transition: "cx 1.4s ease-in-out, cy 1.4s ease-in-out" }}
        >
          <animate
            attributeName="r"
            values="2.6;3.6;2.6"
            dur={`${2.4 + (i % 4) * 0.3}s`}
            begin={`${i * 0.08}s`}
            repeatCount="indefinite"
          />
        </circle>
      ))}

      {/* Last point pulse */}
      <g style={{ transition: "transform 1.4s ease-in-out" }}>
        <circle cx={last.x} cy={last.y} r="5" fill="#5EE0FF">
          <animate attributeName="r" values="5;14;5" dur="2.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.9;0.15;0.9" dur="2.2s" repeatCount="indefinite" />
        </circle>
        <circle cx={last.x} cy={last.y} r="3.4" fill="#FFFFFF">
          <animate attributeName="opacity" values="1;0.7;1" dur="1.6s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}
